import { type tags } from 'typia';
import { type TimeLineData } from '@ardoq/api-types';
/** A single bar: one category and its numeric value. */
export type ChartDataPoint = {
    /** Category name shown on the axis, e.g. an application status, lifecycle phase, or type. Keep it short. Long labels are truncated in the chart. */
    label: string;
    /** Numeric value for this category (the bar height). Must be a plain number, not a formatted string. */
    value: number;
};
/** A single observation in a series: one time and its numeric value. */
export type ChartTimeSeriesPoint = {
    /** A point in time on the x-axis, given as an ISO date or timestamp, e.g. `2024-01`, `2024-01-15`, or `2024-01-15T00:00:00Z`. Points are plotted in chronological order. */
    time: string;
    /** The value at this time. Must be a plain number, not a formatted string. Required: every point needs a value (nulls/gaps are not supported). */
    value: number;
};
export type ChartTimeSeries = {
    name: string;
    label?: string;
    values: ChartTimeSeriesPoint[];
};
/** A single line: a named series and its time-ordered points. */
export type ValidatedChartTimeSeries = {
    /** Series identifier: what this line measures, e.g. an application count or a risk score. Used to distinguish lines when there is more than one. */
    name: string;
    /** Optional display name for this series in the legend. Omit to fall back to `name`. */
    label?: string;
    /** Time-ordered points for this series; at least one is required. Provide them in chronological order. */
    values: ChartTimeSeriesPoint[] & tags.MinItems<1>;
};
export const toTimeLineData = (data: ChartTimeSeries[]): TimeLineData => data.map(({ name, label, values }) => ({
    name,
    label,
    values: values.map(({ time, value }) => ({ _time: time, value })),
}));
export const parseOrNull = <T>(parse: (value: string) => T | null) => (value: string): T | null => {
    try {
        return parse(value);
    }
    catch {
        return null;
    }
};
